// services/sixCatalog/SixCatalogServiceImpl.ts
import { SixCatalogService } from "@/services/abstract/SixCatalogService";
import type { SixesCatalogApiResponse } from "@/types/six/SixDTOs";
import type { FetcherService } from "@/services/abstract/FetcherService";
import { sixEndpoints } from "@/api/endpoints";
/**
 * Implementation of Six catalog service for browsing and searching
 * available Six workspaces.
 *
 * Provides functionality for:
 * - Fetching all Sixes
 * - Searching Sixes by name or description
 *
 * Includes fallback logic to use cached Six data when network
 * requests fail.
 *
 * @see SixCatalogService
 */
export class SixCatalogServiceImpl extends SixCatalogService {
  /**
   * Creates an instance of SixCatalogServiceImpl
   * @param fetcher - Service for making HTTP requests to the backend
   */
  constructor(private readonly fetcher: FetcherService) {
    super();
  }

  /**
   * Retrieves a list of Six workspaces, optionally filtered by search term
   *
   * Falls back to cached Six data (window.sixes) if the API request fails,
   * ensuring the application can continue functioning with stale data when
   * connectivity issues occur.
   *
   * @endpoint GET /sixes or GET /sixes?search={query}
   * @param search - Optional search query to filter Sixes by name or description
   * @returns Array of SixSummary objects representing available Sixes
   * @throws UnauthorizedError if user is not authenticated (caught internally, returns cached data)
   * @tags six, catalog, search
   */
  async fetchSixes(search?: string): Promise<SixesCatalogApiResponse> {
    return await this.fetcher.get<SixesCatalogApiResponse>(sixEndpoints.getSixes);
  }
}
